/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import _ from "lodash";
import Plotly from "plotly.js";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon, Loader, Message, Table } from "semantic-ui-react";
import AFPlot from "../../../Lumber/Plotting/AFPlotly";
import { getPrecision } from "../../../Lumber/ValuePrecisionConverter";
import { getColorFromPalette } from "./MapStyles";
import { HeatmapData, PlotProps, useData, useGraph, useViewerDispatch } from "./StateManagement";
import { DateTime } from "luxon";

type Props = { resizing: boolean; graphStyle?: React.CSSProperties };

const Graph = ({ resizing, graphStyle }: Props) => {
	const { updateSelectedPlotIds, updateLegendStatus } = useViewerDispatch();
	const { heatmapData, heatmapDataAllDates, plotData, dataLayerNames, hideExclusions } = useData();
	const { xAxis, yAxis, xOrder, chartType } = useGraph();
	const [legendStatus, setLegendStatus] = useState({});
	const [isSelecting, setIsSelecting] = useState(false);

	useEffect(() => {
		updateLegendStatus(legendStatus);
	}, [legendStatus, updateLegendStatus]);

	const buildBoxPlotData = useCallback(
		(
			dataMap: _.Dictionary<HeatmapData>,
			broadGroups: _.Dictionary<PlotProps[]>,
			treatmentMap: { [key: string]: string },
			legendStatus: { [key: string]: boolean | "legendonly" },
			jitter: boolean = true,
		) => {
			const compoundGroupedData: {
				[key: string]: { y: number[]; x: string[]; customdata: string[][] };
			} = {};

			_.forOwn(broadGroups, (plots, broadGroup) => {
				compoundGroupedData[broadGroup] = { y: [], x: [], customdata: [] };
				_.forEach(plots, (plot) => {
					if (dataMap[plot.plotId]) {
						compoundGroupedData[broadGroup].y.push(dataMap[plot.plotId].plotAnalysisResultValue);
						compoundGroupedData[broadGroup].x.push(dataLayerNames[plot.group ?? 0] ?? "None");
						compoundGroupedData[broadGroup].customdata.push([plot.plotId, plot.treatment.toString()]);
					}
				});
			});

			const traces: Partial<Plotly.BoxPlotData>[] = [];
			let i = 0;
			_.forOwn(compoundGroupedData, (traceData, group) => {
				if (group !== "") {
					traces.push({
						y: traceData.y,
						x: traceData.x,
						customdata: traceData.customdata,
						boxpoints: jitter ? "all" : "outliers",
						jitter: jitter ? 0.5 : 0,
						pointpos: 0,
						type: "box",
						marker: {
							color: getColorFromPalette(Number(group) - 1),
						},
						line: {
							dash: i < 20 ? "solid" : "dash",
						},
						name: treatmentMap[group],
						visible: legendStatus[treatmentMap[group]],
					});
					i++;
				}
			});

			return traces;
		},
		[dataLayerNames],
	);

	const buildBarChartData = useCallback(
		(
			dataMap: _.Dictionary<HeatmapData>,
			broadGroups: _.Dictionary<PlotProps[]>,
			treatmentMap: { [key: string]: string },
			legendStatus: { [key: string]: boolean | "legendonly" },
		) => {
			const compoundGroupedData: {
				[key: string]: { y: number[]; x: string[]; customdata: string[] };
			} = {};

			_.forOwn(broadGroups, (plots, broadGroup) => {
				compoundGroupedData[broadGroup] = { y: [], x: [], customdata: [] };
				const averages = _.chain(plots)
					.filter((p) => dataMap[p.plotId] !== undefined)
					.groupBy((p) => dataLayerNames[p.group ?? 0])
					.mapValues((p) => _.meanBy(p, (plot) => dataMap[plot.plotId].plotAnalysisResultValue))
					.mapKeys((_, g) => (g === "undefined" ? "None" : g))
					.entries()
					.value();
				const averagesTransposed = _.zip(...averages);
				compoundGroupedData[broadGroup] = {
					y: averagesTransposed[1] as number[],
					x: averagesTransposed[0] as string[],
					customdata: Array<string>(averagesTransposed[0].length).fill(broadGroup),
				};
			});

			const traces: Partial<Plotly.PlotData>[] = [];
			_.forOwn(compoundGroupedData, (traceData, group) => {
				if (group !== "") {
					traces.push({
						y: traceData.y,
						x: traceData.x,
						customdata: traceData.customdata,
						type: "bar",

						marker: {
							color: getColorFromPalette(Number(group) - 1) + "80",
							line: {
								color: getColorFromPalette(Number(group) - 1),
								width: 2,
							},
						},
						name: treatmentMap[group],
						visible: legendStatus[treatmentMap[group]],
					});
				}
			});

			return traces;
		},
		[dataLayerNames],
	);

	const buildTableData = useCallback(
		(
			dataMap: _.Dictionary<HeatmapData>,
			broadGroups: _.Dictionary<PlotProps[]>,
			treatmentMap: { [key: string]: string },
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			_legendStatus: { [key: string]: boolean | "legendonly" },
		) => {
			const tableData: string[][] = [];
			const compound = _.mapValues(broadGroups, (p) => _.groupBy(p, "group"));
			const compoundAverages = _.mapValues(compound, (g) =>
				_.mapValues(g, (p) => [
					_.meanBy(
						_.filter(p, (plot) => dataMap[plot.plotId] !== undefined),
						(plot) => dataMap[plot.plotId].plotAnalysisResultValue,
					),
					p.length,
				]),
			);

			_.forOwn(compoundAverages, (groups, treatment) => {
				if (treatment) {
					const row: string[] = [treatmentMap[treatment]];
					if (dataLayerNames.length === 0) {
						const average = groups[0];
						if (average !== undefined) {
							const rounded = getPrecision(average[0]) as number;
							row.push(`${rounded} (n=${average[1]})`);
						} else {
							row.push("-");
						}
					}
					for (let i = 1; i < dataLayerNames.length; i++) {
						const average = groups[i];
						if (average !== undefined) {
							const rounded = getPrecision(average[0]) as number;
							row[i] = rounded ? `${rounded} (n=${average[1]})` : "-";
						} else {
							row[i] = "-";
						}
					}
					tableData.push(row);
				}
			});
			// const transposedTableData = _.zip(...tableData);

			// Plotly doesn't have types for table data yet
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const traces: Partial<any>[] = [];

			traces.push({
				type: "table",
				header: {
					values: [
						dataLayerNames.length > 0 ? [undefined, { span: dataLayerNames.length - 1, title: xAxis }] : undefined,
						["Treatments"].concat(dataLayerNames.length === 0 ? "All Plots" : dataLayerNames.slice(1)),
					],
					font: { family: "Inter Tight", weight: 600, size: 20 },
					height: 30,
				},
				cells: {
					values: tableData,
					font: { family: "Inter Tight", size: 18 },
					// align: ["left"],
					height: 28,
				},
			});

			return traces;
		},
		[dataLayerNames, xAxis],
	);

	const buildLineChartData = useCallback(
		(
			dataMapGrouped: _.Dictionary<HeatmapData[]>,
			broadGroups: _.Dictionary<PlotProps[]>,
			treatmentMap: { [key: string]: string },
			legendStatus: { [key: string]: boolean | "legendonly" },
		) => {
			const compoundGroupedData: {
				[key: string]: { y: number[]; x: string[]; customdata: string[] };
			} = {};
			_.forOwn(broadGroups, (plots, broadGroup) => {
				compoundGroupedData[broadGroup] = { y: [], x: [], customdata: [] };

				const allEntries = _.flatMap(plots, (p) => dataMapGrouped[p.plotId] ?? []);
				const averages = _.chain(allEntries)
					.groupBy("dateCollected")
					.mapValues((entries) => _.meanBy(entries, "plotAnalysisResultValue"))
					.mapKeys((_, g) => (g === "undefined" ? "None" : g))
					.entries()
					.sortBy(([date]) => DateTime.fromISO(date).toMillis())
					.value();

				if (averages.length) {
					const averagesTransposed = _.zip(...averages);
					compoundGroupedData[broadGroup] = {
						y: averagesTransposed[1] as number[],
						x: averagesTransposed[0] as string[],
						customdata: Array<string>(averagesTransposed[0]?.length ?? 0).fill(broadGroup),
					};
				}
			});

			const traces: Partial<Plotly.PlotData>[] = [];

			_.forOwn(compoundGroupedData, (traceData, group) => {
				if (group !== "") {
					traces.push({
						y: traceData.y,
						x: traceData.x,
						customdata: traceData.customdata,
						type: "scatter",

						marker: {
							color: getColorFromPalette(Number(group) - 1) + "80",
							line: {
								color: getColorFromPalette(Number(group) - 1),
								width: 2,
							},
						},
						name: treatmentMap[group],
						visible: legendStatus[treatmentMap[group]],
					});
				}
			});

			return traces;
		},
		[],
	);

	const boxPlotLayout: Partial<Plotly.Layout> = useMemo(
		() => ({
			boxmode: "group",
			margin: {
				b: 75,
				l: chartType === "table" ? 15 : 75,
				r: 15,
				t: chartType === "table" ? 75 : 15,
			},
			autosize: true,
			paper_bgcolor: "#fff0",
			plot_bgcolor: "#fff0",
			// colorway: COLOR_PALETTE,
			dragmode: "pan",
			title:
				chartType === "table"
					? {
							text: yAxis,
							font: { family: "Inter Tight", weight: 600, size: 28 },
						}
					: undefined,
			yaxis: {
				title: { text: yAxis, font: { family: "Inter Tight", weight: 600, size: 20 } },
			},
			xaxis: {
				showticklabels: xOrder.length > 1 || xOrder[0] !== "None",
				automargin: true,
				title:
					chartType !== "line"
						? { text: xAxis, font: { family: "Inter Tight", weight: 600, size: 20 }, standoff: 10 }
						: undefined,
				tickfont: { family: "Inter Tight", size: 16 },
				categoryorder: "array",
				categoryarray: xOrder,
			},
			showlegend: false,
			bargroupgap: 0.15,
		}),
		[yAxis, xAxis, xOrder, chartType],
	);

	const getPlotDataParams = useCallback(
		(plotData: PlotProps[], heatmapData: HeatmapData[], hideExclusions: boolean) => {
			let filteredPlots = _.filter<PlotProps>(
				plotData,
				(pd) => (!hideExclusions || !pd.status?.includes("Excluded")) && !!pd.group,
			);
			filteredPlots = filteredPlots.length > 0 ? filteredPlots : plotData;
			const broadGroups = _.groupBy(filteredPlots, "treatment");
			const treatmentMap = _.chain(filteredPlots)
				.groupBy("treatment")
				.mapValues((p) => p[0].treatmentName)
				.value();
			const dataMap = _.keyBy(heatmapData, "plotId");
			const dataMapGrouped = _.groupBy(heatmapData, "plotId");
			return { broadGroups, treatmentMap, dataMap, dataMapGrouped };
		},
		[],
	);

	// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
	const graphData = useMemo(() => {
		if ((heatmapData || heatmapDataAllDates) && plotData?.length) {
			if (heatmapData?.length === 0 && heatmapDataAllDates?.length === 0) return [];

			const { broadGroups, treatmentMap, dataMap } = heatmapData?.length
				? getPlotDataParams(plotData, heatmapData, hideExclusions)
				: { broadGroups: {}, treatmentMap: {}, dataMap: {} };

			const {
				broadGroups: broadGroupsAllDates,
				treatmentMap: treatmentMapAllDates,
				dataMapGrouped: dataMapGroupedAllDates,
			} = heatmapDataAllDates?.length
				? getPlotDataParams(plotData, heatmapDataAllDates, hideExclusions)
				: { broadGroups: {}, treatmentMap: {}, dataMapGrouped: {} };

			switch (chartType) {
				case "box-jitter":
					return buildBoxPlotData(dataMap, broadGroups, treatmentMap, legendStatus);
				case "box":
					return buildBoxPlotData(dataMap, broadGroups, treatmentMap, legendStatus, false);
				case "bar":
					return buildBarChartData(dataMap, broadGroups, treatmentMap, legendStatus);
				case "line":
					return buildLineChartData(dataMapGroupedAllDates, broadGroupsAllDates, treatmentMapAllDates, legendStatus);
				case "table":
					return buildTableData(dataMap, broadGroups, treatmentMap, legendStatus);
			}
		}
	}, [
		heatmapData,
		heatmapDataAllDates,
		plotData,
		chartType,
		hideExclusions,
		getPlotDataParams,
		legendStatus,
		buildBoxPlotData,
		buildBarChartData,
		buildLineChartData,
		buildTableData,
	]);

	useEffect(() => {
		setIsSelecting(false);
		updateSelectedPlotIds([]);
	}, [updateSelectedPlotIds, graphData]);

	return useMemo(
		() => (
			<>
				{graphData?.length === 0 && (
					<Message style={{ margin: "0", borderRadius: "8px" }} warning icon={<Icon className="warning" />}>
						<Message.Content>No heatmap data found for this trial.</Message.Content>
					</Message>
				)}
				<div
					style={{
						width: "100%",
						height: chartType === "table" ? "40%" : "60%",
						maxHeight: "500px",
						background: "#ffffff80",
						borderRadius: "8px",
						marginTop: "10px",
						padding: "4px",
						position: "relative",
						...graphStyle,
					}}
				>
					{graphData && !resizing ? (
						graphData.length > 0 ? (
							chartType !== "table" ? (
								<AFPlot
									style={{ width: "100%", height: "100%" }}
									layout={boxPlotLayout}
									data={graphData}
									config={{ responsive: true }}
									useResizeHandler
									onDeselect={() => {
										setIsSelecting(false);
										updateSelectedPlotIds([]);
									}}
									onSelecting={(e) => {
										setIsSelecting(true);
										if (e.points.length > 0) {
											const selectedPlotIds = _.map(e.points, (pt) => {
												if (!Array.isArray(pt.customdata)) return "";
												return pt.customdata[0] as string;
											});
											updateSelectedPlotIds(selectedPlotIds);
										}
									}}
									onSelected={(e) => {
										if (e.points.length > 0) {
											const selectedPlotIds = _.map(e.points, (pt) => {
												if (!Array.isArray(pt.customdata)) return "";
												return pt.customdata[0] as string;
											});
											updateSelectedPlotIds(selectedPlotIds);
										}
									}}
									onHover={(e) => {
										if (isSelecting) return;
										if (e.points.length === 1) {
											const point = e.points[0];
											const selectedX = point.x;
											if (chartType === "box-jitter") {
												// custom data is plotId for point based charts
												const grouppedPlots = point.data.customdata.reduce<string[]>((acc, cd, i) => {
													if (!Array.isArray(cd)) return acc;
													const x = point.data.x[i];
													const plotId = cd[0] as string;
													if (x === selectedX) {
														acc.push(plotId);
													}
													return acc;
												}, []);
												updateSelectedPlotIds(grouppedPlots);
											} else if (chartType === "line" && plotData) {
												// custom data is treatmentId
												const grouppedPlots = plotData
													.filter(
														(pd) =>
															(!hideExclusions || !pd.status?.includes("Excluded")) &&
															pd.treatment.toString() === point.customdata,
													)
													.map((p) => p.plotId);
												updateSelectedPlotIds(grouppedPlots);
											} else if (plotData) {
												// custom data is treatmentId for non point based charts
												const grouppedPlots = plotData
													.filter(
														(pd) =>
															(!hideExclusions || !pd.status?.includes("Excluded")) &&
															dataLayerNames[pd.group ?? 0] === selectedX &&
															pd.treatment.toString() === point.customdata,
													)
													.map((p) => p.plotId);
												updateSelectedPlotIds(grouppedPlots);
											}
										} else {
											updateSelectedPlotIds([]);
										}
									}}
									onUnhover={() => {
										if (isSelecting) return;
										updateSelectedPlotIds([]);
									}}
								/>
							) : (
								<>
									<div
										style={{
											width: "100%",
											textAlign: "center",
											fontWeight: "600",
											fontSize: "28px",
											padding: "0.4em",
											lineHeight: "28px",
										}}
									>
										{typeof boxPlotLayout.title === "string" ? boxPlotLayout.title : (boxPlotLayout.title?.text ?? "")}
									</div>
									<Table
										celled
										striped
										style={{
											margin: "0 8px 8px",
											width: "stretch",
										}}
									>
										<Table.Header>
											{graphData[0].header.values[0] && (
												<Table.Row>
													{_.map(graphData[0].header.values[0], (v: { span: number; title: string } | undefined, i) => (
														<Table.HeaderCell textAlign="center" key={`header-${i}`} colSpan={v?.span}>
															{v?.title}
														</Table.HeaderCell>
													))}
												</Table.Row>
											)}
											<Table.Row>
												{_.map(graphData[0].header.values[1] as string[], (v: string, i: number) => (
													<Table.HeaderCell key={`header-${i}`} textAlign={i === 0 ? "right" : "center"}>
														{v}
													</Table.HeaderCell>
												))}
											</Table.Row>
										</Table.Header>
										<Table.Body>
											{_.map(graphData[0].cells.values as string[][], (row: string[], i) => (
												<Table.Row key={`row-${i}`}>
													{_.map(row, (cell, j) => (
														<Table.Cell key={`cell-${i}-${j}`} textAlign={j === 0 ? "right" : "center"}>
															{cell}
														</Table.Cell>
													))}
												</Table.Row>
											))}
										</Table.Body>
									</Table>
								</>
							)
						) : (
							<></>
						)
					) : (
						<Loader active />
					)}
				</div>
				{chartType !== "table" && (
					<div
						style={{
							background: "#ffffff80",
							marginTop: "1em",
							height: 0,
							padding: 8,
							borderRadius: 8,
							display: "flex",
							flexDirection: "column",
							flexWrap: "wrap",
							flexGrow: 1,
							gap: 5,
						}}
					>
						{_.map(graphData, (trace) => {
							const idx = (
								chartType === "box-jitter" || chartType === "box" ? trace.customdata[0][1] : trace.customdata[0]
							) as number;
							return (
								<span
									key={trace.name as string}
									className={`legendItem${trace.visible === "legendonly" ? " disabled" : ""}`}
									onMouseOver={() => {
										if (isSelecting) return;
										if (plotData) {
											const plots = _.reduce<PlotProps, string[]>(
												plotData,
												(acc, curr) => {
													return curr.treatmentName === trace.name &&
														(!hideExclusions || !curr.status?.includes("Excluded"))
														? acc.concat(curr.plotId)
														: acc;
												},
												[],
											);
											updateSelectedPlotIds(plots);
										}
									}}
									onMouseOut={() => {
										if (isSelecting) return;
										updateSelectedPlotIds([]);
									}}
									onClick={() => {
										const toggle = (trace.visible ?? true) === true ? "legendonly" : true;
										setLegendStatus((prev) => {
											const next = { ...prev, [trace.name!]: toggle };
											return next;
										});
									}}
								>
									<div
										style={{
											width: 15,
											height: 15,
											border: `2.5px solid ${getColorFromPalette(idx - 1)}`,
											backgroundColor: `${getColorFromPalette(idx - 1)}80`,
											marginRight: 8,
										}}
									/>
									{idx} - {trace.name}
								</span>
							);
						})}
					</div>
				)}
			</>
		),
		[
			graphData,
			resizing,
			boxPlotLayout,
			chartType,
			updateSelectedPlotIds,
			isSelecting,
			plotData,
			dataLayerNames,
			hideExclusions,
			graphStyle,
		],
	);
};

export default Graph;
